import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, logout, error, user, sessionStatus } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin" && sessionStatus === "approved") {
      nav("/admin", { replace: true });
    }
  }, [user, sessionStatus, nav]);

  // If session got revoked, silently clear and bounce back to the login form
  useEffect(() => {
    if (sessionStatus === "revoked") {
      logout();
    }
  }, [sessionStatus, logout]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const status = await login(email, password);
    setBusy(false);
    if (status === "approved") nav("/admin", { replace: true });
    // if "pending" or "revoked": stay here, render the waiting / blocked screen
  };

  // Pending screen (waiting for admin approval)
  if (sessionStatus === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-white" data-testid="login-pending">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="font-mono-data text-[10px] uppercase tracking-[0.4em] text-neutral-500">
            En attente d'approbation
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl">Demande envoyée</h1>
          <p className="text-sm text-neutral-600">
            Votre connexion est en attente d'approbation par l'administrateur.
            Cette page se mettra à jour automatiquement dès que votre accès aura été validé.
          </p>
          <div className="flex items-center justify-center gap-2 font-mono-data text-[11px] uppercase tracking-[0.2em] text-neutral-400 pt-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            En attente
          </div>
          <Button
            variant="outline"
            onClick={logout}
            className="rounded-none border-black hover:bg-neutral-50 mt-4"
            data-testid="login-pending-cancel"
          >
            Annuler
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: image */}
      {/* Left: image */}
      <div className="hidden lg:flex relative bg-black items-center justify-center overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_celine-ticket-admin/artifacts/9enudngo_image.png"
          alt="Céline Dion"
          className="max-h-[60vh] w-auto object-contain"
        />
        <div className="absolute top-12 left-12 right-12 flex items-start justify-between text-white pointer-events-none">
          <div className="font-serif-display text-2xl tracking-wide drop-shadow">
            Céline Dion · Billetterie
          </div>
        </div>
        <div className="absolute bottom-12 left-12 font-mono-data text-[11px] uppercase tracking-[0.3em] text-white/80 drop-shadow">
          Accès administrateur
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-8"
          data-testid="login-form"
        >
          <div className="space-y-3">
            <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-neutral-500">
              Administration
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-none">
              Connexion
            </h1>
            <p className="text-sm text-neutral-500">
              Espace réservé. Identifiants requis pour gérer le stock.
            </p>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-neutral-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-neutral-700">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black h-11"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error ? (
            <div
              className="border border-red-500 text-red-600 text-sm px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-none bg-black hover:bg-neutral-800 text-white h-11 tracking-wide"
            data-testid="login-submit-button"
          >
            {busy ? "Connexion…" : "Se connecter"}
          </Button>

          <a
            href="/vente"
            className="block text-center text-xs text-neutral-500 hover:text-black tracking-widest uppercase"
            data-testid="login-back-vente"
          >
            ← Aller à la billetterie publique
          </a>
        </form>
      </div>
    </div>
  );
}
